import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  useCallback,
  useReducer,
  useEffect,
} from "react";
import { API_SOCKET_URL } from "../utils/constants";
import { showToast } from "../utils/toast";
import { formatSpeaker } from "../utils/speakerLabel";

const StreamingContext = createContext(null);

const initialState = { captions: [], status: "Not Connected" };

function reducer(state, action) {
  switch (action.type) {
    case "ADD_TRANSCRIPT":
      return { ...state, captions: [...state.captions, action.payload] };
    case "SET_STATUS":
      return { ...state, status: action.payload };
    case "RESET":
      return initialState;
    default:
      return state;
  }
}


function formatTime(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

 



// The server may send HTML entities ("&#39;", "&quot;", "&amp;", ...). Decode
// them all to plain characters first, then escape exactly once, so every
// character displays as it was spoken and nothing is double-escaped.
function decodeEntities(str) {
  const el = document.createElement("textarea"); // textarea never runs scripts
  el.innerHTML = str;
  return el.value;
}

function escapeHtml(str) {
  return decodeEntities(String(str ?? ""))
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// A final caption from a *different* speaker with this many words or fewer
// ("yeah", "mm-hmm", a mis-diarized fragment) is treated as part of the current
// speaker's block instead of opening a new speaker header. Set to 0 to disable.
const MAX_WORDS_TO_IGNORE_SPEAKER_SWITCH = 2;

// Defined at module level — never changes, never causes re-renders.
const PCM_PROCESSOR_CODE = `
class PcmProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this._buf = [];
    this._len = 0;
    this._TARGET = 320; // 20ms @ 16kHz = 320 samples
  }
  process(inputs) {
    const input = inputs[0][0];
    if (!input) return true;
    this._buf.push(new Float32Array(input));
    this._len += input.length;
    if (this._len >= this._TARGET) {
      const merged = new Float32Array(this._len);
      let offset = 0;
      for (const f of this._buf) { merged.set(f, offset); offset += f.length; }
      const pcm = new Int16Array(merged.length);
      for (let i = 0; i < merged.length; i++) {
        pcm[i] = Math.max(-32768, Math.min(32767, merged[i] * 32768));
      }
      this.port.postMessage(pcm.buffer, [pcm.buffer]);
      this._buf = [];
      this._len = 0;
    }
    return true;
  }
}
registerProcessor('pcm-processor', PcmProcessor);
`;

export function StreamingProvider({ children }) {
  // ── Refs (never trigger re-renders) ───────────────────────────────────────
  const websocketRef    = useRef(null);
  const audioContextRef = useRef(null);
  const mediaStreamRef  = useRef(null);
  const workletRef      = useRef(null);
  const timerRef        = useRef(null);
  const isStartingRef   = useRef(false);
  const audioStartedRef = useRef(false); // guard: initMedia runs exactly once per session
  const isStreamingRef  = useRef(false);
  const isStoppingRef   = useRef(false);
  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const sessionConfigRef = useRef(null);
  const lastTranscriptArrivedRef = useRef(null);
  /** If the host socket never receives `event_live`, promote UI after `session_started` (backend variance). */
  const captionsLiveFallbackTimerRef = useRef(null);
  /** Same mic stream as PCM pipeline; used only for LiveAudioVisualizer in host UI. */
  const streamMediaRecorderRef = useRef(null);

  const speakerRef     = useRef(null);

    // ── Lifecycle flags ───────────────────────────────────────────────────────
  const isMountedRef        = useRef(true);

  // ── State ─────────────────────────────────────────────────────────────────
  const [isStreaming,   setIsStreaming]   = useState(false);

  const [justReconnected, setJustReconnected] = useState(false);

  const [numConnected,  setNumConnected]  = useState(0);
  const [seconds,       setSeconds]       = useState(0);
  const [streamMediaRecorder, setStreamMediaRecorder] = useState(null);

  const [{ captions, status }, dispatch] = useReducer(reducer, initialState);

  // ── Transcript formatting ─────────────────────────────────────────────────
  const appendTimeStamp = useCallback((newCaption) => {
    const start = new Date(newCaption.startedAt).getTime();
    const now   = Date.now();
    const secs  = Math.max(0, Math.floor((now - start) / 1000));
    const rawText = String(newCaption.transcript ?? "").trim();
    const text    = escapeHtml(rawText);
    const endsSentence = /[.?!]$/.test(rawText);

    const wordCount = rawText ? rawText.split(/\s+/).length : 0;
    const isSpeakerChange =
      speakerRef.current !== null && speakerRef.current !== newCaption.speaker;

    // A very short fragment from another speaker is most likely a backchannel
    // or a diarization blip, so keep it inside the current speaker's block.
    const isBlip =
      isSpeakerChange && wordCount <= MAX_WORDS_TO_IGNORE_SPEAKER_SWITCH;

    let res;
    if (speakerRef.current === newCaption.speaker || isBlip) {
      res = " " + text;
    } else {
      res = "<br>";
      res += `<div style="display:flex; justify-content: space-between;">
                <strong>${escapeHtml(formatSpeaker(newCaption.speaker))}</strong>
                <span>${formatTime(secs)}</span>
              </div>`;
      res += " " + text;
      speakerRef.current = newCaption.speaker;
    }
    if (endsSentence) res += "<br>";

    // Return a new object instead of mutating the incoming caption.
    return { ...newCaption, transcript: res };
  }, []);

  const enqueueTranscript = useCallback((caption) => {
    if (!isMountedRef.current) return;
    dispatch({
      type:    "ADD_TRANSCRIPT",
      payload: { ...appendTimeStamp(caption), status: "translated" },
    });
  }, [appendTimeStamp]);

  // ── initMedia ─────────────────────────────────────────────────────────────
  // Starts the AudioWorklet pipeline and begins sending PCM to the server.
  // Safe to call multiple times — the audioStartedRef guard ensures it only
  // actually runs once per session.
  const initMedia = useCallback(async () => {
    if (audioStartedRef.current) return; // already running
    audioStartedRef.current = true;

    try {
      const stream = mediaStreamRef.current;
      if (!stream) throw new Error("Mic stream not available");

      const blob    = new Blob([PCM_PROCESSOR_CODE], { type: "application/javascript" });
      const blobUrl = URL.createObjectURL(blob);

      const audioContext = new AudioContext({ sampleRate: 16000 });
      await audioContext.audioWorklet.addModule(blobUrl);
      URL.revokeObjectURL(blobUrl);

      const source  = audioContext.createMediaStreamSource(stream);
      const worklet = new AudioWorkletNode(audioContext, "pcm-processor");

      audioContextRef.current = audioContext;
      workletRef.current      = worklet;

      worklet.port.onmessage = (e) => {
        const ws = websocketRef.current;
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(e.data); // raw Int16Array buffer — PCM linear16
        }
      };

      // Connect source → worklet. Do NOT connect to destination (mic feedback).
      source.connect(worklet);

      // "Captions live" is set only when `event_live` arrives (parity with iOS).
      console.log("[Audio] PCM worklet started — streaming audio to server");
    } catch (err) {
      audioStartedRef.current = false; // allow retry on failure
      console.error("[Audio] initMedia failed:", err);
      showToast("Microphone error: " + err.message, { type: "error" });
    }
  }, []); // stable — reads refs only

  // ── cleanup ───────────────────────────────────────────────────────────────
  const cleanup = useCallback(() => {
    isStoppingRef.current = true;

    // Stop timer
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (lastTranscriptArrivedRef.current) {
      window.clearInterval(lastTranscriptArrivedRef.current);
      lastTranscriptArrivedRef.current = null;
    }

    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (captionsLiveFallbackTimerRef.current) {
      window.clearTimeout(captionsLiveFallbackTimerRef.current);
      captionsLiveFallbackTimerRef.current = null;
    }
    reconnectAttemptsRef.current = 0;
    sessionConfigRef.current = null;

    try {
      const r = streamMediaRecorderRef.current;
      if (r && r.state !== "inactive") r.stop();
    } catch { /* ignore */ }
    streamMediaRecorderRef.current = null;
    setStreamMediaRecorder(null);

    // Disconnect and close AudioWorklet
    try { workletRef.current?.port.close(); } catch { /* ignore */ }
    workletRef.current = null;

    // Close AudioContext (stops all audio processing)
    try { audioContextRef.current?.close(); } catch { /* ignore */ }
    audioContextRef.current = null;

    // Stop all mic tracks (removes browser mic indicator)
    try { mediaStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    mediaStreamRef.current = null;

    // Close WebSocket
    try { websocketRef.current?.close(); } catch { /* ignore */ }
    websocketRef.current = null;

    // Reset audio guard
    audioStartedRef.current = false;
    isStreamingRef.current = false;
    speakerRef.current = null; // next session starts with a fresh speaker header

    setIsStreaming(false);
    dispatch({ type: "RESET" });
    setNumConnected(0);
    setSeconds(0);
    // Reset after cleanup completes so onclose handlers from this close
    // are ignored as intentional.
    setTimeout(() => {
      isStoppingRef.current = false;
    }, 0);
  }, []);

  const attachStreamingSocketHandlers = useCallback((ws) => {
    ws.onopen = () => {
      const cfg = sessionConfigRef.current;
      if (!cfg) return;

      console.log("[WS] Connected — sending start_event");
      ws.send(JSON.stringify({
        type:    "start_event",
        lang:    cfg.speechLanguage || "en",
        eventId: cfg.eventResults.id,
        userId:  cfg.loginResults?.id,
      }));
    };

    ws.onmessage = (evt) => {
      let received;
      try { received = JSON.parse(evt.data); } catch { return; }

      switch (received?.type) {
        case "connected":
          break;

        // Host: Deepgram pipeline ready — start PCM (see STREAMING_DOC.MD).
        case "session_started": {
          reconnectAttemptsRef.current = 0;
          if (reconnectTimerRef.current) {
            window.clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
          }

          if (!timerRef.current) {
            timerRef.current = window.setInterval(() => {
              setSeconds((prev) => prev + 1);
            }, 1000);
          }
          initMedia();
          // Still "Getting ready…" until `event_live` (iOS / PM-194 parity).
          if (captionsLiveFallbackTimerRef.current) {
            window.clearTimeout(captionsLiveFallbackTimerRef.current);
          }
          captionsLiveFallbackTimerRef.current = window.setTimeout(() => {
            captionsLiveFallbackTimerRef.current = null;
            dispatch({ type: "SET_STATUS", payload: "Captions Live" });
          }, 4000);
          break;
        }

        // Session is live for listeners — this is when the host UI shows "Captions live".
        case "event_live": {
          reconnectAttemptsRef.current = 0;
          if (reconnectTimerRef.current) {
            window.clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
          }
          if (captionsLiveFallbackTimerRef.current) {
            window.clearTimeout(captionsLiveFallbackTimerRef.current);
            captionsLiveFallbackTimerRef.current = null;
          }

          if (!timerRef.current) {
            timerRef.current = window.setInterval(() => {
              setSeconds((prev) => prev + 1);
            }, 1000);
          }
          initMedia();
          dispatch({ type: "SET_STATUS", payload: "Captions Live" });
          break;
        }

        case "viewer_count_update":
          if (typeof received.currentCount === "number") {
            setNumConnected(received.currentCount);
          }
          break;

        case "transcript":
          if (received.isFinal === true) {
            console.log("[Transcript]", received.speaker, received.startedAt, received);
            enqueueTranscript(received);
        
            lastTranscriptArrivedRef.current = new Date().getTime();
          }
          break;

        case "error":
          showToast(received.message, { type: "error" });
          break;

        default:
          break;
      }
    };

    ws.onclose = (evt) => {
      console.log("[WS] Closed:", evt.code, evt.reason);

      if (isStoppingRef.current || !isStreamingRef.current) return;

      const cfg = sessionConfigRef.current;
      if (!cfg) {
        dispatch({ type: "SET_STATUS", payload: "Connection Lost" });
        return;
      }

      const maxRetries = 3;
      if (reconnectAttemptsRef.current >= maxRetries) {
        dispatch({ type: "SET_STATUS", payload: "Connection Lost" });
        return;
      }

      reconnectAttemptsRef.current += 1;
      dispatch({ type: "SET_STATUS", payload: "Reconnecting…" });
      const delayMs = reconnectAttemptsRef.current * 2000;

      reconnectTimerRef.current = window.setTimeout(() => {
        try {
          const nextWs = new WebSocket(
            `${API_SOCKET_URL}?token=${cfg.loginResults.token}&eventId=${cfg.eventResults.id}`
          );
          websocketRef.current = nextWs;
          attachStreamingSocketHandlers(nextWs);
        } catch {
          dispatch({ type: "SET_STATUS", payload: "Connection Lost" });
        }
      }, delayMs);
    };

    ws.onerror = (err) => {
      console.error("[WS] Error:", err);
    };
  }, [initMedia]);


   // ── start ─────────────────────────────────────────────────────────────────
  const notifyOrgUsers = useCallback(async ({ eventResults, loginResults }) => {
    if (!eventResults?.id)     return;

    try {
      // 3. Open WebSocket.
      const ws = new WebSocket(
        `${API_SOCKET_URL}?token=${loginResults.token}`
      );
      websocketRef.current = ws;

      ws.onopen = () => {
     
        ws.send(JSON.stringify({
          type:    "notify_scheduled_event",
          eventId: eventResults.id,
          name: eventResults.name,
          scheduledToStartAt: eventResults.scheduledToStartAt,
          location: eventResults.location,
          organizationId: eventResults.organization_id.id
        }));
      };

      ws.onmessage = (evt) => {
        let received;
        try { received = JSON.parse(evt.data); } catch { return; }

        switch (received?.type) {

          case "connected":
            // Server acknowledged the socket — nothing to do, start_event
            // is sent from onopen.
            break;

          case "org_users_notified": {
            cleanupAfterNotify()
          }
            break;

          case "error":
            showToast(received.message, { type: "error" });
            break;

          default:
            break;
        }
      };

      ws.onclose = (evt) => {
        console.log("[WS] Closed:", evt.code, evt.reason);

      };

      ws.onerror = (err) => {
        console.error("[WS] Error:", err);
      };

    } catch (err) {
      console.error("[Streaming] start failed:", err);
      showToast("Failed to start streaming: " + err.message, { type: "error" });
      cleanup();
    }
  }, [cleanup]);


  // ── start ─────────────────────────────────────────────────────────────────
  const start = useCallback(async ({ eventResults, loginResults, speechLanguage }) => {
    if (!eventResults?.id)     return;
    if (isStartingRef.current) return;
    if (isStreaming)            return;

    isStartingRef.current = true;

    try {
      isStoppingRef.current = false;

      // 1. Acquire mic stream once — shared by initMedia and cleanup.
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current  = stream;
      audioStartedRef.current = false;
      speakerRef.current      = null;
      sessionConfigRef.current = { eventResults, loginResults, speechLanguage };
      reconnectAttemptsRef.current = 0;

      // Host captions screen: bar visualizer (parallel to AudioWorklet PCM upload).
      try {
        const preferredMime = "audio/webm";
        const recorder = MediaRecorder.isTypeSupported(preferredMime)
          ? new MediaRecorder(stream, { mimeType: preferredMime })
          : new MediaRecorder(stream);
        recorder.start(250);
        streamMediaRecorderRef.current = recorder;
        setStreamMediaRecorder(recorder);
      } catch (e) {
        console.warn("[Streaming] Live visualizer MediaRecorder:", e);
        streamMediaRecorderRef.current = null;
        setStreamMediaRecorder(null);
      }

      // 2. Update UI immediately so the user sees feedback.
      setIsStreaming(true);
      isStreamingRef.current = true;
      dispatch({ type: "RESET" });
      dispatch({ type: "SET_STATUS", payload: "Getting ready…" });
      setSeconds(0);
      setNumConnected(0);

      // 3. Open WebSocket.
      const ws = new WebSocket(
        `${API_SOCKET_URL}?token=${loginResults.token}&eventId=${eventResults.id}`
      );
      websocketRef.current = ws;
      attachStreamingSocketHandlers(ws);

    } catch (err) {
      console.error("[Streaming] start failed:", err);
      showToast("Failed to start streaming: " + err.message, { type: "error" });
      cleanup();
    } finally {
      isStartingRef.current = false;
    }
  }, [isStreaming, attachStreamingSocketHandlers, cleanup]);


  const setSpeechLang = useCallback(async ({ speechLanguage }) => {
    if(sessionConfigRef.current && sessionConfigRef.current.speechLanguage !== speechLanguage){
      websocketRef.current?.send(JSON.stringify({
        type:    "set_speech_lang",
        lang: speechLanguage,
        eventId: sessionConfigRef.current.eventResults.id,
      }));
      sessionConfigRef.current = { ...sessionConfigRef.current, speechLanguage };
    }
  }, []);

  // ── stop ──────────────────────────────────────────────────────────────────
  const stop = useCallback(({ eventId, userId } = {}) => {
    isStoppingRef.current = true;
    try {
      if (websocketRef.current?.readyState === WebSocket.OPEN) {
        websocketRef.current.send(JSON.stringify({
          type: "end-event-stream",
          eventId,
          userId,
        }));
      }
    } catch { /* ignore */ }

    cleanup();
  }, [cleanup]);

  const cleanupAfterNotify = useCallback(()=>{
     // Close WebSocket
    try { websocketRef.current?.close(); } catch { /* ignore */ }
    websocketRef.current = null;
  }, [])

   // Clear justReconnected after a short window (transcript catch-up period)
  useEffect(() => {
    if (!justReconnected) return;
    const t = setTimeout(() => setJustReconnected(false), 4000);
    return () => clearTimeout(t);
  }, [justReconnected]);

  // ── context value ─────────────────────────────────────────────────────────
  const value = useMemo(() => ({
    isStreaming,
    status,
    numConnected,
    seconds,
    durationText: formatTime(seconds),
    captions,
    streamMediaRecorder,
    lastTranscriptArrived: lastTranscriptArrivedRef.current,
    start,
    stop,
    justReconnected,
    setSpeechLang,
    speechLanguage: sessionConfigRef.current?.speechLanguage,
    notifyOrgUsers,
    cleanupAfterNotify,
  }), [isStreaming, status, lastTranscriptArrivedRef.current, sessionConfigRef.current, numConnected, 
       seconds, captions, streamMediaRecorder, start, stop, setSpeechLang, notifyOrgUsers, cleanupAfterNotify]);

  return (
    <StreamingContext.Provider value={value}>
      {children}
    </StreamingContext.Provider>
  );
}

export function useStreaming() {
  const ctx = useContext(StreamingContext);
  if (!ctx) throw new Error("useStreaming must be used within StreamingProvider");
  return ctx;
}